import { Immutable } from "immer";
import React, { useMemo, useState } from "react";
import { t } from "ttag";

import { templates } from "@reactivated";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { eventuallySendMetric } from "../../../api/metrics";
import { cancelOrder } from "../../../api/orders";
import { isoOrderID } from "../../../models/nominals";
import { OrderCancellationInfo } from "../../../models/orders";
import { CancelModal } from "../CancelModal";
import { CancelModalViewState, OTHER_OPTION } from "../models";

const getReason = (
    selectedReason: string | null,
    enteredReason: string,
): string => {
    const reason =
        selectedReason === OTHER_OPTION ? enteredReason : selectedReason;
    return reason || "";
};

export const OrderCancelButton = (
    props: Immutable<templates.OrderCancelButtonTemplate>,
): React.ReactNode => {
    const orderID = isoOrderID.wrap(props.order_id);
    const [viewState, setViewState] = useState<CancelModalViewState>(
        CancelModalViewState.CLOSED,
    );
    const [selectedReason, setSelectedReason] = useState<string | null>(null);
    const [enteredReason, setEnteredReason] = useState<string>("");

    const cancellationInfo: OrderCancellationInfo = useMemo(
        () => ({
            number: props.cancellation_info.number,
            status: props.cancellation_info.status,
            date_placed: new Date(props.cancellation_info.date_placed),
            can_be_cancelled: props.cancellation_info.can_be_cancelled,
            cancellation_window_ends_on: new Date(
                props.cancellation_info.cancellation_window_ends_on,
            ),
            cancellation_reasons: props.cancellation_reasons.map(
                (reason: string) => ({ reason }),
            ),
        }),
        [props.cancellation_info, props.cancellation_reasons],
    );

    const onCancelButtonClick = () => {
        // Record an event that the user has opened the cancel modal.
        eventuallySendMetric({
            name: "OrderCancelButton.open-modal",
            dimensions: [],
            type: "BOOLEAN",
            value: "TRUE",
        });
        setViewState(
            props.cancellation_info.can_be_cancelled
                ? CancelModalViewState.SELECT_REASON
                : CancelModalViewState.ERROR,
        );
    };

    const onRequestClose = () => {
        setViewState(CancelModalViewState.CLOSED);
    };

    const onSelectReason = (reason: string) => {
        setSelectedReason(reason);
        setEnteredReason("");
    };

    const onEnterReason = (reason: string) => {
        setSelectedReason(OTHER_OPTION);
        setEnteredReason(reason);
    };

    const onSubmit = async () => {
        setViewState(CancelModalViewState.SAVING);
        // Record an event that the user has opened the cancel modal.
        eventuallySendMetric({
            name: "OrderCancelButton.cancel-order",
            dimensions: [],
            type: "BOOLEAN",
            value: "TRUE",
        });
        // Cancel the order
        const reason = getReason(selectedReason, enteredReason);
        try {
            await cancelOrder(orderID, reason);
            setViewState(CancelModalViewState.CLOSED);
            // Reload the page so that the new order status is shown.
            const window = getWindow();
            if (window) {
                window.location.reload();
            }
        } catch {
            setViewState(CancelModalViewState.ERROR);
        }
    };

    return (
        <>
            <button
                onClick={onCancelButtonClick}
                disabled={viewState !== CancelModalViewState.CLOSED}
                type="button"
                className="button"
            >
                {t`Cancel Order`}
            </button>
            <CancelModal
                viewState={viewState}
                cancellationInfo={cancellationInfo}
                selectedReason={selectedReason}
                enteredReason={enteredReason}
                onSelectReason={onSelectReason}
                onEnterReason={onEnterReason}
                onRequestClose={onRequestClose}
                onSubmit={onSubmit}
            />
        </>
    );
};
